// src/hooks/useManagementReports.ts
// Reports the user has added to a Management reports group.

import { useCallback, useState } from 'react';

export interface ManagementReportEntry {
  id: string;
  reportKey: string;
  reportName: string;
  group: string;
  addedAt: string;
}

interface UseManagementReportsReturn {
  data: ManagementReportEntry[];
  loading: boolean;
  error: Error | null;
  addToManagement: (input: { reportKey: string; reportName: string; group: string }) => ManagementReportEntry;
  removeFromManagement: (id: string) => void;
  refetch: () => void;
}

const STORAGE_KEY = 'novala:management-reports';

function readFromStorage(): ManagementReportEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as ManagementReportEntry[];
  } catch (e) {
    // ignore
  }
  return [];
}

function writeToStorage(items: ManagementReportEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    // ignore
  }
}

export function useManagementReports(): UseManagementReportsReturn {
  const [data, setData] = useState<ManagementReportEntry[]>(function () { return readFromStorage(); });
  const [loading] = useState(false);
  const [error] = useState<Error | null>(null);

  const addToManagement = useCallback(function (input: { reportKey: string; reportName: string; group: string }) {
    const next: ManagementReportEntry = {
      ...input,
      id: 'mg_' + Date.now().toString(36),
      addedAt: new Date().toISOString(),
    };
    setData(function (prev) {
      const out = [next, ...prev];
      writeToStorage(out);
      return out;
    });
    return next;
  }, []);

  const removeFromManagement = useCallback(function (id: string) {
    setData(function (prev) {
      const out = prev.filter(function (e) { return e.id !== id; });
      writeToStorage(out);
      return out;
    });
  }, []);

  const refetch = useCallback(function () {
    setData(readFromStorage());
  }, []);

  return { data, loading, error, addToManagement, removeFromManagement, refetch };
}